import { useCallback, useEffect, useState } from 'react';
import type { Asset } from './types';

interface UseFileSelectionOptions {
    visibleAssets: Asset[];
    searchTerm: string;
}

interface UseFileSelectionReturn {
    selectionMode: boolean;
    selectedAssets: Set<string>;
    isDownloadingSelected: boolean;
    setIsDownloadingSelected: (v: boolean) => void;
    toggleSelectionMode: () => void;
    toggleAssetSelection: (assetId: string) => void;
    selectAllVisible: () => void;
    clearSelection: () => void;
    exitSelection: () => void;
}

export const useFileSelection = ({
    visibleAssets,
    searchTerm
}: UseFileSelectionOptions): UseFileSelectionReturn => {
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
    const [isDownloadingSelected, setIsDownloadingSelected] = useState(false);

    // Clear selection when search term changes
    useEffect(() => {
        setSelectedAssets(new Set());
    }, [searchTerm]);

    const toggleSelectionMode = useCallback(() => {
        setSelectionMode((prev) => {
            if (prev) setSelectedAssets(new Set());
            return !prev;
        });
    }, []);

    const toggleAssetSelection = useCallback((assetId: string) => {
        setSelectedAssets((prev) => {
            const next = new Set(prev);
            if (next.has(assetId)) {
                next.delete(assetId);
            } else {
                next.add(assetId);
            }
            return next;
        });
    }, []);

    const selectAllVisible = useCallback(() => {
        const visibleIds = visibleAssets.map((a) => String(a.assetId));
        setSelectedAssets(new Set(visibleIds));
    }, [visibleAssets]);

    const clearSelection = useCallback(() => {
        setSelectedAssets(new Set());
    }, []);

    const exitSelection = useCallback(() => {
        setSelectedAssets(new Set());
        setSelectionMode(false);
    }, []);

    return {
        selectionMode,
        selectedAssets,
        isDownloadingSelected,
        setIsDownloadingSelected,
        toggleSelectionMode,
        toggleAssetSelection,
        selectAllVisible,
        clearSelection,
        exitSelection
    };
};
